import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContextType';
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  Grid,
  Alert,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
}

interface PreferencesData {
  job_completion: boolean;
  job_failure: boolean;
}

interface PasswordData {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(3),
}));

const UserSettings: React.FC = () => {
  const { updateProfile, updateNotificationPreferences } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobCompletion, setJobCompletion] = useState(true);
  const [jobFailure, setJobFailure] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Fetch user profile
  const { data: profileData } = useQuery<ProfileData>({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await api.get('/api/user-profile/');
      return response.data;
    },
  });

  // Fetch user preferences
  const { data: preferencesData } = useQuery<PreferencesData>({
    queryKey: ['preferences'],
    queryFn: async () => {
      const response = await api.get('/api/user-preferences/');
      return response.data;
    },
  });

  // Update profile data when fetched
  useEffect(() => {
    if (profileData) {
      setFirstName(profileData.first_name || '');
      setLastName(profileData.last_name || '');
      setEmail(profileData.email || '');
    }
  }, [profileData]);

  // Update preferences data when fetched
  useEffect(() => {
    if (preferencesData) {
      setJobCompletion(preferencesData.job_completion);
      setJobFailure(preferencesData.job_failure);
    }
  }, [preferencesData]);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileData) => {
      await api.put('/api/user-profile/', data);
    },
    onSuccess: () => {
      updateProfile();
      setSuccess('Profile updated successfully');
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
      setSuccess(null);
    },
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: async (data: PreferencesData) => {
      await api.put('/api/user-preferences/', data);
    },
    onSuccess: () => {
      updateNotificationPreferences();
      setSuccess('Preferences updated successfully');
      setError(null);
    },
    onError: (error: Error) => {
      setError(error.message);
      setSuccess(null);
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: PasswordData) => {
      await api.post('/api/change-password/', data);
    },
    onSuccess: () => {
      setPasswordSuccess('Password changed successfully');
      setPasswordError(null);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error: Error) => {
      setPasswordError(error.message);
      setPasswordSuccess(null);
    },
  });

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'first_name') setFirstName(value);
    if (name === 'last_name') setLastName(value);
    if (name === 'email') setEmail(value);
  };

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate({ first_name: firstName, last_name: lastName, email });
  };

  const handleToggle = (preference: keyof PreferencesData) => {
    const newPreferences: PreferencesData = {
      job_completion: preference === 'job_completion' ? !jobCompletion : jobCompletion,
      job_failure: preference === 'job_failure' ? !jobFailure : jobFailure,
    };
    if (preference === 'job_completion') setJobCompletion(!jobCompletion);
    if (preference === 'job_failure') setJobFailure(!jobFailure);
    updatePreferencesMutation.mutate(newPreferences);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    changePasswordMutation.mutate({
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            User Settings
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              Profile Information
            </Typography>
            <form onSubmit={handleProfileSubmit}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={firstName}
                onChange={handleProfileChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={lastName}
                onChange={handleProfileChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={handleProfileChange}
                margin="normal"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                disabled={updateProfileMutation.isLoading}
              >
                Update Profile
              </Button>
            </form>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              Notification Preferences
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={jobCompletion}
                  onChange={() => handleToggle('job_completion')}
                />
              }
              label="Job Completion Notifications"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={jobFailure}
                  onChange={() => handleToggle('job_failure')}
                />
              }
              label="Job Failure Notifications"
            />
          </StyledPaper>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledPaper>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <form onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                disabled={changePasswordMutation.isLoading}
              >
                Change Password
              </Button>
            </form>
            {passwordError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {passwordError}
              </Alert>
            )}
            {passwordSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {passwordSuccess}
              </Alert>
            )}
          </StyledPaper>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        {success && (
          <Grid item xs={12}>
            <Alert severity="success">{success}</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UserSettings; 